/**
 * Scripts with no letter case, where two initials do not read as initials. A Chinese, Japanese or
 * Korean name takes its first glyph (the family name, in the usual order); an Arabic, Hebrew or
 * Thai name takes one letter, because two isolated letters of a joining script look like a word
 * fragment rather than a monogram.
 */
const SINGLE_GLYPH =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Thai}]/u;

/** Grapheme-aware, so "Ólafur" gives "Ó" rather than a bare combining accent. */
const firstGrapheme = (word: string) => {
  const [first] = new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(word);

  return first?.segment ?? '';
};

/**
 * The initials an Avatar draws when there is no photo.
 *
 * First and last word, uppercased: "Zoë O'Brien" → "ZO", "Maximiliane Oyelaran-Featherstonehaugh"
 * → "MO". A single word gives one letter ("Prince" → "P"), as does `max: 1`, which the Avatar
 * passes at 24px and below where two letters stop being legible. Leading punctuation is skipped so
 * a name in quotes or brackets still yields a letter. An empty name returns `''`, which the Avatar
 * turns into the person or group icon.
 */
export const getInitials = (name: string | null | undefined, max: 1 | 2 = 2) => {
  const words = (name ?? '')
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/^[^\p{L}\p{N}]+/u, ''))
    .filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  const first = firstGrapheme(words[0]);

  if (SINGLE_GLYPH.test(first)) {
    return first;
  }

  if (max === 1 || words.length === 1) {
    return first.toLocaleUpperCase();
  }

  return (first + firstGrapheme(words[words.length - 1])).toLocaleUpperCase();
};
